<template>
  <div class="module-container">
    <div class="module-header">
      <div class="module-title">保养检修统计</div>
    </div>
    <div class="module-content">
      <div class="charts-container">
        <div class="chart-wrapper" v-for="(item, index) in maintenanceData" :key="index">
          <div :ref="'chart' + index" class="chart"></div>
          <div class="chart-info">
            <div class="info-item" v-for="(info, idx) in item.info" :key="idx">
              <span class="info-label">{{ info.label }}</span>
              <span class="info-value" :class="info.class" :title="info.value">{{ info.value }}</span>
            </div>
            <div class="more-link" @click="goToMaintenanceRecord(index)">查看更多</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getMaintainInvokeStatistic } from '@/api/greenMan/index'
import { addDate, getCurTimeYMD } from '@/utils/index'

export default {
  name: 'EquipmentMaintenance',
  data() {
    return {
      charts: [],
      maintenanceData: [
        {
          title: '设备保养',
          total: 0,
          completed: 0,
          info: [
            { label: '保养计划总数量', value: '0' },
            { label: '已完成保养数量', value: '0', class: 'completed' },
            { label: '待执行保养数量', value: '0', class: 'pending' }
          ]
        },
        {
          title: '设备检修',
          total: 0,
          completed: 0,
          info: [
            { label: '检修计划总数量', value: '0' },
            { label: '已完成检修数量', value: '0', class: 'completed' },
            { label: '待执行检修数量', value: '0', class: 'pending' }
          ]
        }
      ],
      refreshInterval: null
    }
  },
  mounted() {
    this.fetchMaintainInvokeStatistic()
    window.addEventListener('resize', this.handleResize)
    // 设置定时刷新，每30分钟刷新一次
    this.refreshInterval = setInterval(this.fetchMaintainInvokeStatistic, 1800000)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.charts.forEach(chart => chart.dispose())
    // 清除定时器，避免内存泄漏
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    async fetchMaintainInvokeStatistic() {
      // 先清空数据，确保即使数据不变也能触发重新渲染
      this.maintenanceData = [
        {
          title: '设备保养',
          total: 0,
          completed: 0,
          info: [
            { label: '保养计划总数量', value: '0' },
            { label: '已完成保养数量', value: '0', class: 'completed' },
            { label: '待执行保养数量', value: '0', class: 'pending' }
          ]
        },
        {
          title: '设备检修',
          total: 0,
          completed: 0,
          info: [
            { label: '检修计划总数量', value: '0' },
            { label: '已完成检修数量', value: '0', class: 'completed' },
            { label: '待执行检修数量', value: '0', class: 'pending' }
          ]
        }
      ]
      try {
        const postData = {
          projectId: JSON.parse(window.localStorage.userInfo).projectId,
          startDate: addDate(getCurTimeYMD(), -365),
          endDate: getCurTimeYMD()
        }
        const { data } = await getMaintainInvokeStatistic(postData)
        if (data) {
          // 更新保养数据
          this.maintenanceData[0].total = data.data.planMaintainCount
          this.maintenanceData[0].completed = data.data.finishedMaintainCount
          this.maintenanceData[0].info = [
            { label: '保养计划总数量', value: data.data.planMaintainCount.toString() },
            { label: '已完成保养数量', value: data.data.finishedMaintainCount.toString(), class: 'completed' },
            { label: '待执行保养数量', value: data.data.pendingMaintainCount.toString(), class: 'pending' }
          ]

          // 更新检修数据
          this.maintenanceData[1].total = data.data.planInvokeCount
          this.maintenanceData[1].completed = data.data.finishedInvokeCount
          this.maintenanceData[1].info = [
            { label: '检修计划总数量', value: data.data.planInvokeCount.toString() },
            { label: '已完成检修数量', value: data.data.finishedInvokeCount.toString(), class: 'completed' },
            { label: '待执行检修数量', value: data.data.pendingInvokeCount.toString(), class: 'pending' }
          ]
        }
      } finally {
        // 初始化图表
        this.$nextTick(() => {
          this.initCharts()
        })
      }
    },
    initCharts() {
      this.maintenanceData.forEach((item, index) => {
        const chartEl = this.$refs['chart' + index]
        if (chartEl && chartEl.length > 0) {
          this.createMaintenanceChart(chartEl[0], item.total, item.completed)
        }
      })
    },
    createMaintenanceChart(el, total, completed) {
      const chart = echarts.init(el)
      this.charts.push(chart)

      const percent = Math.round((completed / total) * 100)

      const option = {
        series: [
          {
            type: 'pie',
            radius: ['60%', '75%'],
            center: ['50%', '50%'],
            startAngle: 90,
            labelLine: {
              show: false
            },
            label: {
              show: true,
              position: 'center',
              formatter: function () {
                return '{value|' + total + '}'
              },
              rich: {
                value: {
                  fontSize: 20,
                  fontWeight: 'bold',
                  color: '#fff',
                  lineHeight: 60
                },
              }
            },
            data: [
              {
                value: completed,
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: '#00baff' },
                    { offset: 1, color: '#00ff88' }
                  ]),
                }
              },
              {
                value: total - completed,
                itemStyle: {
                  color: 'rgba(0, 40, 80, 0.5)',
                  borderColor: 'rgba(0, 186, 255, 0.2)',
                  borderWidth: 2
                }
              }
            ],
            emphasis: {
              scale: false
            }
          }
        ]
      }

      chart.setOption(option)
    },
    handleResize() {
      this.charts.forEach(chart => chart.resize())
    },
    goToMaintenanceRecord(index) {
      // 获取用户权限
      const userRoles = JSON.parse(decodeURI(window.localStorage.userRoles))
      // 计算日期参数
      const startDate = addDate(getCurTimeYMD(), -365)
      const endDate = getCurTimeYMD()

      if (index === 0) {
        // 设备保养
        if (userRoles && userRoles.includes('equ_baoyangzhixingjilu')) {
          // 有权限，跳转到保养执行记录
          this.$router.push({
            path: '/deviceStatisticalAnalysis/deviceMainRepair/deviceMain/mainPlanExeMan',
            query: {
              startDate,
              endDate
            }
          });
        } else {
          // 无权限，提示
          this.$message({
            type: 'warning',
            message: '您暂无权限访问保养执行记录'
          });
        }
      } else if (index === 1) {
        // 设备检修
        if (userRoles && userRoles.includes('equ_jianxiuzhixingjilu')) {
          // 有权限，跳转到检修执行记录
          this.$router.push({
            path: '/deviceStatisticalAnalysis/deviceMainRepair/deviceRepair/mainPlanExeMan',
            query: {
              startDate,
              endDate
            }
          });
        } else {
          // 无权限，提示
          this.$message({
            type: 'warning',
            message: '您暂无权限访问检修执行记录'
          });
        }
      }
    }
  }
}
</script>

<style scoped>
.module-container {
  display: flex;
  flex-direction: column;
}

.module-header {
  height: 33px;
  position: relative;
  background-image: url(../../../../static/image/six/title.png);
  background-repeat: no-repeat;
  background-position: left;
  background-size: auto 100%;
}

.module-title {
  position: absolute;
  top: 0;
  left: 38px;
  font-family: YouSheBiaoTiHei;
  font-weight: 400;
  font-size: 19px;
  color: #FFFFFF;
}

.module-content {
  flex: 1;
}

.charts-container {
  display: flex;
  flex-direction: row;
  gap: 10px;
  height: 100%;
  position: relative;
}

.charts-container::before {
  content: '';
  height: 40%;
  position: absolute;
  left: 50%;
  top: 30%;
  width: 2px;
  background: linear-gradient(to bottom, rgba(1, 162, 251, 0), rgba(1, 162, 251, 0.8), rgba(1, 162, 251, 0));
}

.chart-wrapper {
  display: flex;
  align-items: center;
  padding: 15px;
  transition: all 0.3s;
  height: 100%;
  gap: 20px;
  flex: 1;
  border-radius: 4px;
}

.chart {
  width: 120px;
  height: 120px;
}

.chart-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 4px 0;
}

.info-label {
  width: 60%;
  color: #fff;
}

.info-value {
  width: 40%;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-value.completed {
  color: #00ff88;
}

.info-value.pending {
  color: #ff9600;
}

.more-link {
  margin-top: 10px;
  font-size: 12px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s;
  text-align: right;
}

.more-link:hover {
  text-decoration: underline;
}
</style>
